import { useState, useEffect } from 'react'
import {
  Container,
  Card,
  CardContent,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  IconButton,
  Tooltip,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Alert,
  Snackbar,
  MenuItem,
  InputAdornment,
} from '@mui/material'
import RefreshIcon from '@mui/icons-material/Refresh'
import LocalShippingIcon from '@mui/icons-material/LocalShipping'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import SearchIcon from '@mui/icons-material/Search'
import { deliveryTimeService } from '../services'
import { formatDateTime } from '../utils/dateFormat'
import DeliveryTimeBulkDeletePanel from '../components/DeliveryTimeBulkDeletePanel'

const DeliveryTimeManagementPage = () => {
  const [deliveryTimes, setDeliveryTimes] = useState([])
  const [loading, setLoading] = useState(true)
  
  // Pagination State
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [totalItems, setTotalItems] = useState(0)

  // Search State
  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('')
  const [searchLoading, setSearchLoading] = useState(false)

  useEffect(() => {
    const handler = setTimeout(() => {
      if (debouncedSearchQuery !== searchQuery) {
        setPage(0)
        setDebouncedSearchQuery(searchQuery)
      }
    }, 500)

    return () => {
      clearTimeout(handler)
    }
  }, [searchQuery, debouncedSearchQuery])

  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [selectedDeliveryTime, setSelectedDeliveryTime] = useState(null)
  const [editFormData, setEditFormData] = useState({})
  const [actionLoading, setActionLoading] = useState(false)
  const [actionError, setActionError] = useState(null)
  const [successMessage, setSuccessMessage] = useState(null)

  const fetchDeliveryTimes = async () => {
    try {
      setLoading(true)
      const params = {
        page: page + 1,
        limit: rowsPerPage,
      }

      if (debouncedSearchQuery.trim()) {
        params.search = debouncedSearchQuery.trim()
      }

      const data = await deliveryTimeService.getDeliveryTimes(params)
      
      if (data && data.items) {
        setDeliveryTimes(data.items)
        setTotalItems(data.pagination?.total || 0)
      } else {
        setDeliveryTimes(data || [])
        setTotalItems(data?.length || 0)
      }
    } catch (error) {
      console.error('Error fetching delivery times:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchDeliveryTimes()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, debouncedSearchQuery])

  const handleChangePage = (event, newPage) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value)
  }

  const handleSearchSubmit = async (event) => {
    event.preventDefault()
    setPage(0)
    setSearchLoading(true)
    try {
      await fetchDeliveryTimes()
    } finally {
      setSearchLoading(false)
    }
  }

  const handleSearchClear = async () => {
    setSearchQuery('')
    setPage(0)
    setSearchLoading(true)
    try {
      const data = await deliveryTimeService.getDeliveryTimes({ page: 1, limit: rowsPerPage })
      if (data && data.items) {
        setDeliveryTimes(data.items)
        setTotalItems(data.pagination?.total || 0)
      } else {
        setDeliveryTimes(data || [])
        setTotalItems(data?.length || 0)
      }
    } finally {
      setSearchLoading(false)
    }
  }

  const handleEditClick = (dt) => {
    setSelectedDeliveryTime(dt)
    setEditFormData({
      min_delivery_time: dt.min_delivery_time || '',
      min_delivery_unit: dt.min_delivery_unit || 'day',
      max_delivery_time: dt.max_delivery_time || '',
      max_delivery_unit: dt.max_delivery_unit || 'day',
    })
    setEditDialogOpen(true)
  }

  const handleEditClose = () => {
    setEditDialogOpen(false)
    setSelectedDeliveryTime(null)
    setEditFormData({})
    setActionError(null)
  }

  const handleEditChange = (field, value) => {
    setEditFormData({ ...editFormData, [field]: value })
  }

  const handleSaveEdit = async () => {
    try {
      setActionLoading(true)
      setActionError(null)

      if (!selectedDeliveryTime || !selectedDeliveryTime.id) {
        setActionError('Configuration ID is required for update')
        return
      }

      await deliveryTimeService.updateDeliveryTime(selectedDeliveryTime.id, editFormData)
      
      setSuccessMessage('Delivery time updated successfully')
      setTimeout(() => setSuccessMessage(null), 3000)

      handleEditClose()
      fetchDeliveryTimes()
    } catch (err) {
      console.error('Error updating delivery time:', err)
      setActionError(err.response?.data?.message || 'Failed to update delivery time')
    } finally {
      setActionLoading(false)
    }
  }

  const handleDeleteClick = (dt) => {
    setSelectedDeliveryTime(dt)
    setDeleteConfirmOpen(true)
  }

  const handleDeleteConfirm = async () => {
    try {
      setActionLoading(true)
      setActionError(null)

      const response = await deliveryTimeService.deleteDeliveryTime(selectedDeliveryTime.id)

      if (response && response.success) {
        setSuccessMessage('Delivery time deleted successfully')
        setTimeout(() => setSuccessMessage(null), 3000)

        setDeleteConfirmOpen(false)
        setSelectedDeliveryTime(null)
        fetchDeliveryTimes()
      } else {
        setActionError(response?.message || 'Unknown error occurred')
      }
    } catch (err) {
      console.error('Error deleting delivery time:', err)
      setActionError(err.response?.data?.message || 'Failed to delete delivery time')
    } finally {
      setActionLoading(false)
    }
  }

  const handleDeleteCancel = () => {
    setDeleteConfirmOpen(false)
    setSelectedDeliveryTime(null)
  }

  return (
    <Container maxWidth="xl">
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1a1a1a', mb: 1 }}>
            Manage Delivery Time
          </Typography>
          <Typography variant="body1" color="text.secondary">
            View mapped delivery time combinations between Locations and Shops
          </Typography>
        </Box>
        <Tooltip title="Refresh Delivery Times">
          <IconButton onClick={fetchDeliveryTimes} disabled={loading} color="primary">
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Search Bar */}
      <Box sx={{ mb: 3 }}>
        <form onSubmit={handleSearchSubmit}>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', maxWidth: 600 }}>
            <TextField
              label="Search by Shop Id, Location Code or Name"
              value={searchQuery}
              onChange={handleSearchChange}
              size="small"
              sx={{ flexGrow: 1 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#666' }} />
                  </InputAdornment>
                ),
                endAdornment: searchQuery && (
                  <InputAdornment position="end">
                    <Button
                      size="small"
                      onClick={handleSearchClear}
                      sx={{ minWidth: 'auto', p: 0.5, color: '#666' }}
                    >
                      Clear
                    </Button>
                  </InputAdornment>
                ),
              }}
              placeholder="Enter search term..."
            />
            <Button
              type="submit"
              variant="contained"
              disabled={searchLoading}
              sx={{
                bgcolor: '#e90000',
                '&:hover': { bgcolor: '#1B5E20' },
                minWidth: 'auto',
                px: 2
              }}
            >
              {searchLoading ? (
                <CircularProgress size={16} sx={{ color: 'white', mr: 1 }} />
              ) : null}
              {searchLoading ? 'Searching...' : 'Search'}
            </Button>
          </Box>
        </form>
      </Box>

      <DeliveryTimeBulkDeletePanel onDeleted={fetchDeliveryTimes} />

      <Card sx={{ 
        borderRadius: 2, 
        boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)'
      }}>
        <CardContent sx={{ p: 0 }}>
          <TableContainer component={Paper} sx={{ boxShadow: 'none' }}>
            <Table>
              <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}></TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Shop Id</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Location Name</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Location Code</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', align: 'center' }}>Delivery Time Window</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Last Modified</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }} align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : deliveryTimes.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                      <Typography color="text.secondary">No delivery time configurations found. Upload a CSV to map them.</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  deliveryTimes.map((dt) => (
                    <TableRow key={dt.id} hover>
                      <TableCell sx={{ width: 50 }}>
                        <LocalShippingIcon sx={{ color: '#0288d1', opacity: 0.8 }} />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {dt.organization?.organization_name || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                           {dt.location?.name || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', bgcolor: '#f5f5f5', px: 1, py: 0.5, borderRadius: 1, display: 'inline-block' }}>
                          {dt.location?.code || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2" color="primary.main" sx={{ fontWeight: 600 }}>
                          {dt.min_delivery_time} {dt.min_delivery_unit} - {dt.max_delivery_time} {dt.max_delivery_unit}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {formatDateTime(dt.date_modified)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                          <Tooltip title="Edit Delivery Time">
                            <IconButton
                              size="small"
                              onClick={() => handleEditClick(dt)}
                              sx={{ color: '#1976d2' }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete Delivery Time">
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteClick(dt)}
                              sx={{ color: '#d32f2f' }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          
          <TablePagination
            component="div"
            count={totalItems}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50, 100]}
          />
        </CardContent>
      </Card>

      {/* Edit Delivery Time Dialog */}
      <Dialog open={editDialogOpen} onClose={handleEditClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600, bgcolor: '#f8f9fb' }}>Edit Delivery Time Details</DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {actionError && (
            <Alert severity="error" onClose={() => setActionError(null)} sx={{ mb: 2 }}>
              {actionError}
            </Alert>
          )}

          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Updating configuration for <strong>{selectedDeliveryTime?.organization?.organization_name}</strong> at <strong>{selectedDeliveryTime?.location?.name}</strong>
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Min Delivery Time"
                type="number"
                value={editFormData.min_delivery_time || ''}
                onChange={(e) => handleEditChange('min_delivery_time', e.target.value)}
                fullWidth
                size="small"
                disabled={actionLoading}
                required
              />
              <TextField
                label="Unit"
                select
                value={editFormData.min_delivery_unit || 'day'}
                onChange={(e) => handleEditChange('min_delivery_unit', e.target.value)}
                fullWidth
                size="small"
                disabled={actionLoading}
              >
                <MenuItem value="hour">hour</MenuItem>
                <MenuItem value="day">day</MenuItem>
                <MenuItem value="week">week</MenuItem>
              </TextField>
            </Box>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Max Delivery Time"
                type="number"
                value={editFormData.max_delivery_time || ''}
                onChange={(e) => handleEditChange('max_delivery_time', e.target.value)}
                fullWidth
                size="small"
                disabled={actionLoading}
                required
              />
              <TextField
                label="Unit"
                select
                value={editFormData.max_delivery_unit || 'day'}
                onChange={(e) => handleEditChange('max_delivery_unit', e.target.value)}
                fullWidth
                size="small"
                disabled={actionLoading}
              >
                <MenuItem value="hour">hour</MenuItem>
                <MenuItem value="day">day</MenuItem>
                <MenuItem value="week">week</MenuItem>
              </TextField>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0, mt: 2 }}>
          <Button onClick={handleEditClose} disabled={actionLoading} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleSaveEdit}
            variant="contained"
            disabled={actionLoading || !editFormData.min_delivery_time || !editFormData.max_delivery_time}
            sx={{
              bgcolor: '#e90000',
              '&:hover': { bgcolor: '#cc0000' }
            }}
          >
            {actionLoading ? <CircularProgress size={24} color="inherit" /> : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={handleDeleteCancel} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 'bold', color: '#d32f2f' }}>Delete Mapping</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the configuration mapping for Shop <strong>{selectedDeliveryTime?.organization?.organization_name}</strong> and Location <strong>{selectedDeliveryTime?.location?.name}</strong>?
          </Typography>
          <Typography variant="body2" color="error" sx={{ mt: 2 }}>
            This action cannot be undone.
          </Typography>
          {actionError && (
            <Alert severity="error" sx={{ mt: 2 }} onClose={() => setActionError(null)}>
              {actionError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleDeleteCancel} disabled={actionLoading} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
            disabled={actionLoading}
            startIcon={actionLoading ? <CircularProgress size={20} color="inherit" /> : <DeleteIcon />}
          >
            {actionLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={3000}
        onClose={() => setSuccessMessage(null)}
        message={successMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Container>
  )
}

export default DeliveryTimeManagementPage
